import type { Schedule, ScheduleAssetSyncStatus, ScheduleSyncStatus } from '~/types/schedule'
import { isSameDay, isValid, parseISO } from 'date-fns'
import { formatISOToDate } from '~/utils/date'

// Behaviour-linked groups count too: the gateway maps them into groupIds on schedule reads
export const hasScheduleAssignment = (schedule: Schedule): boolean =>
  !!(schedule.groupIds?.length || schedule.zoneIds?.length || schedule.assetIds?.length)

export const getScheduleResourceType = (schedule: Schedule): 'zone' | 'group' | 'selection' => {
  if (!schedule || schedule.assetIds?.length) return 'selection'
  if (schedule.zoneIds?.length) return 'zone'
  if (schedule.groupIds?.length) return 'group'
  return 'selection'
}

export const getScheduleAssetCount = (schedule: Schedule): number => {
  if (!schedule.assetsByStatus) return 0
  return Object.values(schedule.assetsByStatus).reduce((sum, count) => sum + count, 0)
}

export const getScheduleErrorCount = (schedule: Schedule): number => {
  if (!schedule.assetsByStatus) return 0
  return schedule.assetsByStatus.ERROR || 0
}

export interface ScheduleReach {
  total: number
  online: number
  lost: number
  never: number
  offline: number
  pctOnline: number
}

export const getScheduleReach = (schedule: Schedule): ScheduleReach => {
  const comm = schedule.assetsByCommunication
  const online = comm?.online ?? 0
  const lost = comm?.lost_comm ?? 0
  const never = comm?.never_comm ?? 0
  const total = online + lost + never
  return { total, online, lost, never, offline: total - online, pctOnline: total ? online / total : 0 }
}

export interface ScheduleAssetTypeSlice {
  key: string
  count: number
  pct: number
}

export const getScheduleAssetTypes = (schedule: Schedule): ScheduleAssetTypeSlice[] => {
  const byType = schedule.assetsByType
  if (!byType) return []
  const total = Object.values(byType).reduce((sum, count) => sum + count, 0)
  if (total === 0) return []
  return Object.entries(byType)
    .filter(([, count]) => count > 0)
    .map(([key, count]) => ({ key, count, pct: count / total }))
    .sort((a, b) => b.count - a.count)
}

export type ScheduleEmptyReason = 'no-events' | 'no-assets' | 'both'

export const getScheduleEmptyReason = (schedule: Schedule): ScheduleEmptyReason | null => {
  if (schedule.status !== 'draft') return null
  const noEvents = (schedule.eventCount ?? 0) === 0
  const noAssets = getScheduleAssetCount(schedule) === 0
  if (noEvents && noAssets) return 'both'
  if (noEvents) return 'no-events'
  if (noAssets) return 'no-assets'
  return null
}

export const isScheduleUnreachable = (schedule: Schedule): boolean => {
  if (schedule.status !== 'sent' && schedule.status !== 'published') return false
  const reach = getScheduleReach(schedule)
  return reach.total > 0 && reach.online === 0
}

export const canRetrySchedule = (schedule: Schedule): boolean => {
  if (!schedule) return false
  const errors = getScheduleErrorCount(schedule)
  return errors > 0 && (schedule.status === 'sent' || schedule.status === 'published')
}

export const getScheduleSyncStatus = (schedule: Schedule): ScheduleSyncStatus => {
  if (schedule.status === 'draft') return schedule.publishedAt ? 'published-draft' : 'draft'

  if (!schedule.assetsByStatus) return 'unknown'

  const total = getScheduleAssetCount(schedule)
  if (total === 0) return 'no-assets'

  if (schedule.assetsByStatus.UNKNOWN === total) return 'unknown'

  // Distinguish between full and partial errors.
  if (schedule.assetsByStatus.ERROR > 0) {
    if (schedule.assetsByStatus.ERROR === total) return 'error'
    return 'partial-error'
  }

  // If processing has started, we can be in processing, sending or synced states.
  if (schedule.assetsByStatus.PROCESSING > 0) return 'processing'
  if (schedule.assetsByStatus.SENDING > 0) return 'sending'
  if (schedule.assetsByStatus.SENT > 0) return 'sent'

  // If we reach this point, there are no errors and no assets being processed or sent.
  if (schedule.assetsByStatus.SUCCESS === total) return 'synced'

  // But we might have a partial success if some assets are still in unknown state.
  return 'partial-synced'
}

export const getAssetStatusCount = (schedule: Schedule, key: ScheduleAssetSyncStatus): number => {
  return schedule.assetsByStatus?.[key] ?? 0
}

export const getScheduleEventDateText = (schedule: Schedule): string => {
  if (!schedule.from || !schedule.to) {
    return '-'
  }

  const from = parseISO(schedule.from)
  const to = parseISO(schedule.to)

  if (!isValid(from) || !isValid(to)) {
    return '-'
  }

  const fromText = formatISOToDate(from.toISOString())

  if (isSameDay(from, to)) {
    return fromText
  }

  const toText = formatISOToDate(to.toISOString())
  return `${fromText} - ${toText}`
}
